import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { formatCurrencySpaced } from '@/lib/market';
import {
  Home, Coins, TrendingUp,
  MapPin, ArrowUpRight, ArrowDownRight, Wallet, Palette, RefreshCw, Brain, Plus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CHART_PALETTE = ['#ffd879','#d9e2c3','#a8c5da','#c9a86b','#9b8dc4','#8dc4a8','#c4a88d','#8da8c4','#b8c4a8','#c4b88d','#c4c48d','#a8a8c4'];

const fmt = (v, market) => formatCurrencySpaced(v, market);

const GainBadge = ({ pct }) => (
  <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${pct >= 0 ? 'text-[#ffd879]' : 'text-[#c97a3a]'}`}>
    {pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
    {Math.abs(pct).toFixed(1)}%
  </span>
);

const StatCard = ({ label, value, sub }) => (
  <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm h-full">
    <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">{label}</p>
    <p className="text-lg font-bold text-[#1a1a1a]">{value}</p>
    {sub && <p className="text-xs text-[#4a4a4a] mt-0.5">{sub}</p>}
  </div>
);

// ── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ realEstate, gold, art, ip, reValue, goldValue, artValue, ipValue, totalAUM, market, handleTabChange }) {
  const segments = [
    { label: 'Real Estate',        value: reValue },
    { label: 'Gold & Silver',      value: goldValue },
    { label: 'Art & Collectibles', value: artValue },
    { label: 'Intellectual Prop.', value: ipValue },
  ].filter(d => d.value > 0).map((d, i) => ({ ...d, color: CHART_PALETTE[i % CHART_PALETTE.length] }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Stat band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        {[
          { k: 'Total Value', v: fmt(totalAUM, market), delta: 'across all real assets' },
          { k: 'Real Estate', v: fmt(reValue, market), delta: `${realEstate.length} properties` },
          { k: 'Gold & Silver', v: fmt(goldValue, market), delta: `${gold.length} holdings` },
          { k: 'Art & IP', v: fmt(artValue + ipValue, market), delta: `${art.length + ip.length} assets` },
        ].map((cell, i) => (
          <div key={i} style={{ flex: 1, minWidth: 130, padding: '14px 16px', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none', textAlign: 'center' }}>
            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>{cell.k}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: '5px 0 4px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
            <div style={{ fontSize: 10.5, color: '#888' }}>{cell.delta}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      {totalAUM > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'stretch' }}>
          <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 14 }}>Allocation by Asset Type</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={segments.map(d => ({ name: d.label.replace('Intellectual Prop.','IP').replace('Art & Collectibles','Art'), value: d.value, color: d.color }))} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: '#888' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e7 ? `${(v/1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(0)}L` : v} />
                <Tooltip formatter={(v) => [fmt(v, market), 'Value']} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5e5' }} />
                <Bar dataKey="value" radius={[5,5,0,0]}>
                  {segments.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 12 }}>Portfolio Split</div>
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 16, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <PieChart width={150} height={200}>
                  <Pie data={segments.map(d => ({ name: d.label, value: d.value }))} cx={75} cy={100} innerRadius={52} outerRadius={72} dataKey="value" paddingAngle={3}>
                    {segments.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </div>
              <ul style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
                {segments.map((d, i) => {
                  const pct = totalAUM > 0 ? d.value / totalAUM * 100 : 0;
                  return (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 0', borderBottom: i < segments.length - 1 ? '1px solid #f0f0f0' : 'none', fontSize: 10.5 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, color: '#555' }}>{d.label}</span>
                      <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{pct < 0.1 ? '<0.1' : pct.toFixed(1)}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Quick nav */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {[
          { tab: 'real_estate', icon: Home,    label: 'Real Estate',        value: fmt(reValue, market),   sub: `${realEstate.length} properties` },
          { tab: 'gold',        icon: Coins,   label: 'Gold & Commodities', value: fmt(goldValue, market), sub: `${gold.length} holdings` },
          { tab: 'art',         icon: Palette, label: 'Art & Collectibles', value: fmt(artValue, market),  sub: `${art.length} pieces` },
          { tab: 'ip',          icon: Brain,   label: 'Intellectual Prop.', value: fmt(ipValue, market),   sub: `${ip.length} assets` },
        ].map(({ tab, icon: Icon, label, value, sub }) => (
          <button key={tab} onClick={() => handleTabChange(tab)} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px',
            background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
            transition: 'border-color 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffd879'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e5e5'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(255,216,121,0.15)', display: 'grid', placeItems: 'center' }}>
                <Icon size={14} style={{ color: '#c9a000' }} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{label}</div>
                <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{sub}</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>{value}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Tab components ────────────────────────────────────────────────────────────

function RealEstateTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Home className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No real estate properties found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, p) => s + (p.current_value || 0), 0);
  const totalInvested = data.reduce((s, p) => s + (p.purchase_price || 0), 0);
  const totalRent     = data.reduce((s, p) => s + ((p.monthly_rent || 0) * 12), 0);
  const avgYield      = totalValue > 0 ? (totalRent / totalValue * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Value"     value={fmt(totalValue, market)}    sub={`${data.length} properties`} />
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub="cost basis" />
        <StatCard label="Annual Rent"     value={fmt(totalRent, market)}     sub="across rented properties" />
        <StatCard label="Portfolio Yield" value={`${avgYield}%`}             sub="gross rental yield" />
      </div>

      <div className="space-y-3">
        {data.map((p, i) => {
          const gainPct  = p.gain_percent ?? 0;
          const yieldPct = p.rental_yield ?? 0;
          const propType = p.property_type
            ? p.property_type.charAt(0).toUpperCase() + p.property_type.slice(1)
            : '—';
          const statusLabel = p.is_rented ? 'Rented' : 'Vacant';

          return (
            <Card key={i} className="bg-white border-[#e5e5e5]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                      <Home className="w-5 h-5 text-[#ffd879]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1a1a1a] text-sm">{p.name}</p>
                      <p className="text-xs text-[#4a4a4a] flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" />{p.city} · {propType}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-right shrink-0">
                    <div>
                      <p className="text-sm font-bold text-[#1a1a1a]">{fmt(p.current_value, market)}</p>
                      <GainBadge pct={gainPct} />
                    </div>
                    <div>
                      <p className="text-xs text-[#4a4a4a]">Yield</p>
                      <p className="text-sm font-medium text-[#2a2a2a]">{yieldPct > 0 ? `${yieldPct.toFixed(1)}%` : '—'}</p>
                    </div>
                    <Badge className={`text-xs border-0 ${
                      p.is_rented ? 'bg-emerald-100 text-emerald-700' : 'bg-[#f9f9f9] text-[#4a4a4a]'
                    }`}>{statusLabel}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function GoldTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Coins className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No gold & commodities holdings found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, g) => s + (g.units * g.current_price), 0);
  const totalInvested = data.reduce((s, g) => s + (g.units * g.unit_price), 0);
  const avgGain       = totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Value"    value={fmt(totalValue, market)}    sub={`${data.length} holdings`} />
        <StatCard label="Total Invested" value={fmt(totalInvested, market)} sub="cost basis" />
        <StatCard label="Overall Gain"   value={<GainBadge pct={parseFloat(avgGain)} />} sub="since purchase" />
      </div>

      <div className="border border-[#e5e5e5] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#dae4c4]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Instrument</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Type</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Units</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Avg Cost</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Current Price</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Value</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Gain</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Maturity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e5e5e5] bg-white">
            {data.map((g, i) => (
              <tr key={i} className="hover:bg-[#f9f9f9] transition-colors">
                <td className="px-4 py-3 font-medium text-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <Coins className="w-3.5 h-3.5 text-[#ffd879] shrink-0" />{g.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{g.type}</Badge>
                </td>
                <td className="px-4 py-3 text-right text-[#4a4a4a]">{g.units}</td>
                <td className="px-4 py-3 text-right text-[#4a4a4a]">{fmt(g.unit_price, market)}</td>
                <td className="px-4 py-3 text-right text-[#2a2a2a]">{fmt(g.current_price, market)}</td>
                <td className="px-4 py-3 text-right font-semibold text-[#1a1a1a]">{fmt(g.units * g.current_price, market)}</td>
                <td className="px-4 py-3 text-right"><GainBadge pct={g.gain_pct} /></td>
                <td className="px-4 py-3 text-right text-xs text-[#4a4a4a]">{g.maturity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ArtTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Palette className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No art & collectibles found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, a) => s + (a.current_value || 0), 0);
  const totalInvested = data.reduce((s, a) => s + (a.purchase_price || 0), 0);
  const avgGain       = totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Collection Value" value={fmt(totalValue, market)}                    sub={`${data.length} pieces`} />
        <StatCard label="Total Cost Basis" value={fmt(totalInvested, market)}                 sub="acquisition cost" />
        <StatCard label="Unrealised Gain"  value={fmt(totalValue - totalInvested, market)}    sub="appreciation" />
        <StatCard label="Avg. Return"      value={<GainBadge pct={parseFloat(avgGain)} />}    sub="since acquisition" />
      </div>

      <div className="space-y-3">
        {data.map((a, i) => (
          <Card key={i} className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                    <Palette className="w-5 h-5 text-[#ffd879]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a1a] text-sm">{a.name}</p>
                    <p className="text-xs text-[#4a4a4a] mt-0.5">{a.artist} · {a.category} · {a.purchase_year}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right shrink-0">
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Cost</p>
                    <p className="text-sm text-[#4a4a4a]">{fmt(a.purchase_price, market)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Current Est.</p>
                    <p className="text-sm font-bold text-[#1a1a1a]">{fmt(a.current_value, market)}</p>
                    <GainBadge pct={a.gain_pct} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{a.type}</Badge>
                    {a.insured && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Insured</Badge>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function CashTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Wallet className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No cash & equivalents found</p>
      </div>
    );
  }

  const totalBalance = data.reduce((s, c) => s + (c.balance || 0), 0);
  const avgRate      = data.length > 0 ? (data.reduce((s, c) => s + (c.interest_rate || 0), 0) / data.length).toFixed(2) : '0.00';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Balance"   value={fmt(totalBalance, market)} sub={`${data.length} accounts / instruments`} />
        <StatCard label="Avg. Interest"   value={`${avgRate}%`}            sub="blended rate p.a." />
        <StatCard label="Annual Income"   value={fmt(totalBalance * parseFloat(avgRate) / 100, market)} sub="estimated interest income" />
      </div>

      <div className="border border-[#e5e5e5] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#dae4c4]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Account / Instrument</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Institution</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Balance</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Rate</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Maturity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e5e5e5] bg-white">
            {data.map((c, i) => (
              <tr key={i} className="hover:bg-[#f9f9f9] transition-colors">
                <td className="px-4 py-3 font-medium text-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5 text-[#ffd879] shrink-0" />{c.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{c.type}</Badge>
                </td>
                <td className="px-4 py-3 text-[#4a4a4a]">{c.institution}</td>
                <td className="px-4 py-3 text-right font-semibold text-[#1a1a1a]">{fmt(c.balance, market)}</td>
                <td className="px-4 py-3 text-right text-emerald-600">{c.interest_rate}%</td>
                <td className="px-4 py-3 text-right text-xs text-[#4a4a4a]">{c.maturity || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IPTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Brain className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No intellectual property holdings found</p>
      </div>
    );
  }

  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const totalCost     = data.reduce((s, d) => s + (d.purchase_price || 0), 0);
  const totalRoyalty  = data.reduce((s, d) => s + (d.annual_royalty || 0), 0);
  const avgGain       = totalCost > 0 ? ((totalValue - totalCost) / totalCost * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="IP Portfolio Value" value={fmt(totalValue, market)}   sub={`${data.length} assets`} />
        <StatCard label="Total Cost Basis"   value={fmt(totalCost, market)}    sub="acquisition cost" />
        <StatCard label="Annual Royalties"   value={fmt(totalRoyalty, market)} sub="recurring income" />
        <StatCard label="Overall Gain"       value={<GainBadge pct={parseFloat(avgGain)} />} sub="since acquisition" />
      </div>

      <div className="space-y-3">
        {data.map((d, i) => {
          const gainPct = d.gain_pct ?? (d.purchase_price ? ((d.current_value - d.purchase_price) / d.purchase_price * 100) : 0);
          return (
            <Card key={i} className="bg-white border-[#e5e5e5]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                      <Brain className="w-5 h-5 text-[#ffd879]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1a1a1a] text-sm">{d.name}</p>
                      <p className="text-xs text-[#4a4a4a] mt-0.5">
                        {d.type}{d.creator ? ` · ${d.creator}` : ''}
                        {d.royalty_rate ? ` · ${d.royalty_rate}% royalty` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-6 text-right shrink-0">
                    <div className="w-24">
                      <p className="text-xs text-[#4a4a4a] mb-1">Cost</p>
                      <p className="text-sm font-medium text-[#1a1a1a]">{fmt(d.purchase_price, market)}</p>
                    </div>
                    <div className="w-28">
                      <p className="text-xs text-[#4a4a4a] mb-1">Annual Royalty</p>
                      <p className="text-sm font-medium text-emerald-600">{fmt(d.annual_royalty, market)}</p>
                    </div>
                    <div className="w-28">
                      <p className="text-xs text-[#4a4a4a] mb-1">Current Est.</p>
                      <p className="text-sm font-bold text-[#1a1a1a]">{fmt(d.current_value, market)}</p>
                      <GainBadge pct={gainPct} />
                    </div>
                    <div className="w-24">
                      <p className="text-xs text-[#4a4a4a] mb-1">Category</p>
                      <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{d.type}</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function AlternativesTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No alternatives found</p>
      </div>
    );
  }

  const totalInvested = data.reduce((s, a) => s + (a.aum_share || 0), 0);
  const totalNav      = data.reduce((s, a) => s + (a.nav || 0), 0);
  const avgYTD        = data.length > 0 ? (data.reduce((s, a) => s + (a.ytd || 0), 0) / data.length).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Committed" value={fmt(totalInvested, market)} sub={`${data.length} funds`} />
        <StatCard label="Current NAV"     value={fmt(totalNav, market)}      sub="marked to NAV" />
        <StatCard label="Avg. YTD Return" value={`${avgYTD}%`}              sub="across portfolio" />
        <StatCard label="Unrealised Gain" value={fmt(totalNav - totalInvested, market)} sub="NAV vs. cost" />
      </div>

      <div className="space-y-3">
        {data.map((a, i) => (
          <Card key={i} className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#ffd879]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a1a] text-sm">{a.name}</p>
                    <p className="text-xs text-[#4a4a4a] mt-0.5">{a.type} · {a.strategy}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right shrink-0">
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Committed</p>
                    <p className="text-sm font-medium text-[#2a2a2a]">{fmt(a.aum_share, market)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">NAV</p>
                    <p className="text-sm font-bold text-[#1a1a1a]">{fmt(a.nav, market)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">YTD</p>
                    <GainBadge pct={a.ytd} />
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Since Inc.</p>
                    <GainBadge pct={a.since_inc} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function OthersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const market = user?.market || 'IN';

  const activeTab = searchParams.get('tab') || 'overview';
  const handleTabChange = (tab) => setSearchParams({ tab });

  const [loading, setLoading] = useState(true);
  const [realEstate, setRealEstate]   = useState([]);
  const [gold, setGold]               = useState([]);
  const [art, setArt]                 = useState([]);
  const [cash, setCash]               = useState([]);
  const [alternatives, setAlternatives] = useState([]);
  const [ip, setIp]                   = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      try {
        const [reRes, goldRes, artRes, cashRes, altRes, ipRes] = await Promise.all([
          axios.get(`${API}/real-estate/portfolio`).catch(() => ({ data: null })),
          axios.get(`${API}/other-assets/gold`).catch(() => ({ data: [] })),
          axios.get(`${API}/other-assets/art`).catch(() => ({ data: [] })),
          axios.get(`${API}/other-assets/cash`).catch(() => ({ data: [] })),
          axios.get(`${API}/other-assets/alternatives`).catch(() => ({ data: [] })),
          axios.get(`${API}/other-assets/ip`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setRealEstate(reRes.data?.properties || []);
        setGold(goldRes.data || []);
        setArt(artRes.data || []);
        setCash(cashRes.data || []);
        setAlternatives(altRes.data || []);
        setIp(ipRes.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const reValue   = realEstate.reduce((s, p) => s + (p.current_value || 0), 0);
  const goldValue = gold.reduce((s, g) => s + (g.units * g.current_price), 0);
  const artValue  = art.reduce((s, a) => s + (a.current_value || 0), 0);
  const cashValue = cash.reduce((s, c) => s + (c.balance || 0), 0);
  const altValue  = alternatives.reduce((s, a) => s + (a.nav || 0), 0);
  const ipValue   = ip.reduce((s, d) => s + (d.current_value || 0), 0);
  const totalAUM  = reValue + goldValue + artValue + cashValue + altValue + ipValue;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <RefreshCw className="w-8 h-8 text-[#ffd87a] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <div style={{ width: 46, height: 46, borderRadius: 14, background: '#dae4c4', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Coins size={22} color="#1a1a1a" />
          </div>
          <div>
            <div style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 4 }}>Asset Dashboard</div>
            <h1 style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, fontSize: 34, letterSpacing: '-0.02em', lineHeight: 1.05, color: '#2a2a2a', margin: 0 }}>Others</h1>
          </div>
        </div>
        <button
          onClick={() => navigate('/import')}
          className="flex items-center gap-2 px-4 py-2 bg-[#ffd87a] hover:bg-[#f5cc6a] text-black text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Holdings
        </button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-[#f5f5f5] border border-[#e5e5e5] mb-6 h-auto gap-1 p-1 w-fit flex-wrap">
          <TabsTrigger value="overview"       className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="gold"           className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Coins className="w-3.5 h-3.5" /> Gold & Commodities
          </TabsTrigger>
          <TabsTrigger value="art"            className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Palette className="w-3.5 h-3.5" /> Art & Collectibles
          </TabsTrigger>
          <TabsTrigger value="cash"           className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Cash & Equivalents
          </TabsTrigger>
          <TabsTrigger value="alternatives"   className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Alternatives
          </TabsTrigger>
          <TabsTrigger value="ip"             className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Brain className="w-3.5 h-3.5" /> Intellectual Property
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab realEstate={realEstate} gold={gold} art={art} ip={ip} reValue={reValue} goldValue={goldValue} artValue={artValue} ipValue={ipValue} totalAUM={totalAUM} market={market} handleTabChange={handleTabChange} />
        </TabsContent>
        <TabsContent value="gold">
          <GoldTab data={gold} market={market} />
        </TabsContent>
        <TabsContent value="art">
          <ArtTab data={art} market={market} />
        </TabsContent>
        <TabsContent value="cash">
          <CashTab data={cash} market={market} />
        </TabsContent>
        <TabsContent value="alternatives">
          <AlternativesTab data={alternatives} market={market} />
        </TabsContent>
        <TabsContent value="ip">
          <IPTab data={ip} market={market} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
